/**
 * Resolving "what should this child do next".
 *
 * Pathways declare far more lessonIds than the static library actually holds
 * (111 referenced across 22 pathways, 6 authored). A naive
 * `pathway.lessonIds.find(id => !completed.includes(id))` therefore hands back
 * an id that resolves to `undefined`, and the home screen falls through to its
 * "you've finished everything" celebration — shown to a child who has done
 * nothing at all.
 *
 * Every id returned from this module is resolved against SAMPLE_LESSONS first,
 * so callers can trust that a returned lesson can actually be opened.
 */
import { SAMPLE_LESSONS } from '../data/pathways';
import type { AgeBand, Lesson, Pathway } from '../types';

/** Lesson ids in `pathway` that exist in the library and can actually be opened. */
export function realLessonIds(pathway: Pathway | null | undefined): string[] {
  if (!pathway) return [];
  return pathway.lessonIds.filter((id) => Boolean(SAMPLE_LESSONS[id]));
}

/** The first real, unfinished lesson in this pathway — or null if there is none. */
export function nextLessonInPathway(
  pathway: Pathway | null | undefined,
  completedIds: string[],
): Lesson | null {
  const id = realLessonIds(pathway).find((lid) => !completedIds.includes(lid));
  return id ? SAMPLE_LESSONS[id] : null;
}

/**
 * Any lesson in the library the child hasn't finished yet.
 * Keeps the home screen from dead-ending when their chosen pathway has no
 * authored content — they still get offered something real to do.
 *
 * When `ageBand` is given the walk stays inside that band: the library's
 * insertion order starts with the 4-7 spark lessons, so an uncapped walk hands
 * a 15-year-old a preschool lesson the moment their pathway runs dry. Returning
 * null here is honest — the caller shows its "more is coming" state instead.
 */
export function anyUnfinishedLesson(
  completedIds: string[],
  ageBand?: AgeBand,
): Lesson | null {
  const lesson = Object.values(SAMPLE_LESSONS).find(
    (l) => !completedIds.includes(l.id) && (!ageBand || l.ageBand === ageBand),
  );
  return lesson ?? null;
}

/**
 * What the home screen should offer next: the child's own pathway first, then
 * anything else in their age band they haven't done. Null once the band (or,
 * without a band, the library) is exhausted — never a wrong-age lesson.
 */
export function resolveTodayLesson(
  pathway: Pathway | null | undefined,
  pathwayCompletedIds: string[],
  allCompletedIds: string[],
  ageBand?: AgeBand,
): Lesson | null {
  return (
    nextLessonInPathway(pathway, pathwayCompletedIds) ??
    anyUnfinishedLesson(allCompletedIds, ageBand)
  );
}

/**
 * Whether the "you've finished everything" celebration is honest.
 * Requires the child to have actually completed at least one lesson — the bug
 * this guards against was showing that celebration on a brand-new profile.
 */
export function hasEarnedCompletionCelebration(
  todayLesson: Lesson | null,
  allCompletedIds: string[],
): boolean {
  return todayLesson === null && allCompletedIds.length > 0;
}
